import {
  PROJECT_SCOPED_SERVER_SETTING_KEYS,
  type ProjectId,
  type ProjectScopedServerSettingKey,
  type ProjectSettingsOverrides,
  type ServerSettings,
} from "@afar-research/contracts";

export { PROJECT_SCOPED_SERVER_SETTING_KEYS };
export type { ProjectScopedServerSettingKey, ProjectSettingsOverrides };

const PROJECT_SCOPED_KEYS: ReadonlySet<string> = new Set(PROJECT_SCOPED_SERVER_SETTING_KEYS);

export function isProjectScopedSettingKey(key: string): key is ProjectScopedServerSettingKey {
  return PROJECT_SCOPED_KEYS.has(key);
}

/**
 * The settings an AFAR Project actually runs under: its Environment's answers
 * with its own overrides laid on top.
 *
 * An absent override is not missing information — the project inherits, and
 * only a deliberate override is ever stored. That is why this derives the
 * effective view on read instead of materializing a copy per project: a
 * project that has never disagreed with its Environment carries no record at
 * all, and an Environment-level change reaches it immediately.
 */
export function resolveProjectSettings(
  settings: ServerSettings,
  projectId: ProjectId | null,
): ServerSettings {
  if (projectId === null) return settings;
  const overrides = settings.projectSettingsOverrides[projectId];
  if (overrides === undefined) return settings;
  const applied = Object.fromEntries(
    Object.entries(overrides).filter(([, value]) => value !== undefined),
  );
  return Object.keys(applied).length === 0 ? settings : { ...settings, ...applied };
}

/** Whether `key` is answered by the project itself rather than its Environment. */
export function isProjectSettingOverridden(
  settings: ServerSettings,
  projectId: ProjectId | null,
  key: ProjectScopedServerSettingKey,
): boolean {
  if (projectId === null) return false;
  return settings.projectSettingsOverrides[projectId]?.[key] !== undefined;
}

export type ProjectSettingSource = "overridden" | "inherited";

/**
 * Where the keys' effective values come from for one project. `mixed` means
 * the keys disagree with each other, which a row renders as an explicit state
 * rather than silently picking one.
 */
export function projectSettingSource(
  settings: ServerSettings,
  projectId: ProjectId | null,
  keys: readonly ProjectScopedServerSettingKey[],
): ProjectSettingSource | "mixed" {
  if (keys.length === 0) return "inherited";
  let overridden = 0;
  for (const key of keys) {
    if (isProjectSettingOverridden(settings, projectId, key)) overridden += 1;
  }
  if (overridden === 0) return "inherited";
  return overridden === keys.length ? "overridden" : "mixed";
}

/**
 * The projects on this Environment that answer any of `keys` for themselves.
 * An Environment-level edit leaves these untouched, so a row can name them and
 * offer to clear them rather than appearing to have changed something it did
 * not.
 */
export function listProjectSettingOverrides(
  settings: ServerSettings,
  keys: readonly ProjectScopedServerSettingKey[],
): readonly ProjectId[] {
  return Object.entries(settings.projectSettingsOverrides).flatMap(([projectId, overrides]) =>
    keys.some((key) => overrides[key] !== undefined) ? [projectId as ProjectId] : [],
  );
}

/**
 * The patch entry that drops `keys` from one project. Removing the last
 * override returns the whole entry to null so the project stops carrying a
 * record it no longer needs.
 */
export function clearProjectSettingOverrides(
  settings: ServerSettings,
  projectId: ProjectId,
  keys: readonly ProjectScopedServerSettingKey[],
): ProjectSettingsOverrides | null {
  const current = settings.projectSettingsOverrides[projectId];
  if (current === undefined) return null;
  const dropped = new Set<string>(keys);
  const remaining = Object.fromEntries(
    Object.entries(current).filter(([key, value]) => !dropped.has(key) && value !== undefined),
  );
  return Object.keys(remaining).length === 0 ? null : (remaining as ProjectSettingsOverrides);
}

/**
 * The patch entry that makes `projectId` answer `values` for itself, keeping
 * whatever it already overrode. Only project-scopable keys can be written
 * here; anything else belongs to the Environment.
 */
export function setProjectSettingOverrides(
  settings: ServerSettings,
  projectId: ProjectId,
  values: Partial<ProjectSettingsOverrides>,
): ProjectSettingsOverrides | null {
  const current = settings.projectSettingsOverrides[projectId] ?? {};
  const next = Object.fromEntries(
    Object.entries({ ...current, ...values }).filter(
      ([key, value]) => value !== undefined && isProjectScopedSettingKey(key),
    ),
  );
  return Object.keys(next).length === 0 ? null : (next as ProjectSettingsOverrides);
}
